/*
 * Copyright (c) 2026. Legalbird eine Marke der R&S Internet Jewels GmbH
 */

import React from "react";
import Grid from "@mui/material/Grid";
import MenuItem from "@mui/material/MenuItem";
import useForm from "../../../hooks/useForm";
import {
  dateFieldDefault,
  isPastDate,
  matchRegexp,
  requiredFieldDefault,
  textFieldDefault,
} from "../../../services/validationRules";
import ValidatorSelect from "../../Validator/ValidatorSelect";
import ValidatorTextField from "../../Validator/ValidatorTextField";
import ValidatorForm from "../../Validator/ValidatorForm";
import SubmitButton from "../../Button/SubmitButton";
import _ from "lodash";
import { Box } from "@mui/material";
import { processCostSupportStyle } from "../processCostSupportStyle";
import { ProcessCostSupportFormProps } from "../../../types/ProcessCostSupport";

const Petitioner = ({ pcs, onSubmit, isLoading }: ProcessCostSupportFormProps) => {
  const initialValues = {
    "petitioner.gender": pcs.petitioner.gender,
    "petitioner.givenName": pcs.petitioner.givenName,
    "petitioner.familyName": pcs.petitioner.familyName,
    "petitioner.birthName": pcs.petitioner.birthName || "",
    "petitioner.birthDate": pcs.petitioner.birthDate || "",
    "petitioner.residenceAddress.streetAddress": pcs.petitioner.residenceAddress.streetAddress,
    "petitioner.residenceAddress.postalCode": pcs.petitioner.residenceAddress.postalCode,
    "petitioner.residenceAddress.addressLocality": pcs.petitioner.residenceAddress.addressLocality,
    telephone: pcs.telephone,
  };

  const { values, errors, handleChange, handleSubmit, handleBlur, registerValidators, touchedValues } = useForm({
    initialValues,
    onSubmit,
    identifier: pcs,
  });

  return (
    <>
      <Box sx={processCostSupportStyle.sectionHeadline}>Angaben zur Person</Box>
      <ValidatorForm onSubmit={handleSubmit}>
        <Grid container sx={processCostSupportStyle.grid}>
          <Grid
            size={{
              xs: 12,
              md: 6,
              lg: 4,
            }}
          >
            <ValidatorSelect
              displayEmpty
              label={"Anrede"}
              value={values["petitioner.gender"] ?? ""}
              onChange={handleChange}
              onBlur={handleBlur}
              name={"petitioner.gender"}
              error={!!errors["petitioner.gender"]}
              helperText={errors["petitioner.gender"]}
              MenuProps={{
                slotProps: {
                  paper: {
                    sx: { maxHeight: 200 },
                  },
                },
              }}
            >
              <MenuItem value={""}>Keine Anrede</MenuItem>
              <MenuItem value={"female"}>Frau</MenuItem>
              <MenuItem value={"male"}>Herr</MenuItem>
            </ValidatorSelect>
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 6,
              lg: 4,
            }}
          >
            <ValidatorTextField
              name="petitioner.givenName"
              label="Vorname"
              value={values["petitioner.givenName"]}
              onChange={handleChange}
              onBlur={handleBlur}
              validators={[...requiredFieldDefault, ...textFieldDefault]}
              registerValidators={registerValidators}
              error={!!errors["petitioner.givenName"]}
              helperText={errors["petitioner.givenName"]}
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 6,
              lg: 4,
            }}
          >
            <ValidatorTextField
              name="petitioner.familyName"
              label="Nachname"
              value={values["petitioner.familyName"]}
              onChange={handleChange}
              onBlur={handleBlur}
              validators={[...requiredFieldDefault, ...textFieldDefault]}
              registerValidators={registerValidators}
              error={!!errors["petitioner.familyName"]}
              helperText={errors["petitioner.familyName"]}
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 6,
              lg: 4,
            }}
          >
            <ValidatorTextField
              name="petitioner.birthName"
              label="Geburtsname (optional)"
              value={values["petitioner.birthName"]}
              onChange={handleChange}
              onBlur={handleBlur}
              validators={textFieldDefault}
              registerValidators={registerValidators}
              error={!!errors["petitioner.birthName"]}
              helperText={errors["petitioner.birthName"]}
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 6,
              lg: 4,
            }}
          >
            <ValidatorTextField
              name="petitioner.birthDate"
              label="Geburtsdatum"
              value={values["petitioner.birthDate"]}
              onChange={handleChange}
              onBlur={handleBlur}
              validators={[
                ...requiredFieldDefault,
                ...dateFieldDefault,
                {
                  validator: isPastDate,
                  message: "Das Datum muss in der Vergangenheit liegen",
                },
              ]}
              registerValidators={registerValidators}
              error={!!errors["petitioner.birthDate"]}
              helperText={errors["petitioner.birthDate"]}
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 6,
              lg: 4,
            }}
          >
            <ValidatorTextField
              name="petitioner.residenceAddress.streetAddress"
              label="Straße und Hausnummer"
              value={values["petitioner.residenceAddress.streetAddress"]}
              onChange={handleChange}
              onBlur={handleBlur}
              validators={[
                ...requiredFieldDefault,
                {
                  validator: matchRegexp,
                  params: "^[a-zA-ZäÄöÖüÜß]{2}.*[0-9]+.*$",
                  message: "Straße und Hausnummer im richtigen Format angeben",
                },
              ]}
              registerValidators={registerValidators}
              error={!!errors["petitioner.residenceAddress.streetAddress"]}
              helperText={errors["petitioner.residenceAddress.streetAddress"]}
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 6,
              lg: 4,
            }}
          >
            <ValidatorTextField
              name="petitioner.residenceAddress.postalCode"
              label="Postleitzahl"
              value={values["petitioner.residenceAddress.postalCode"]}
              onChange={handleChange}
              onBlur={handleBlur}
              validators={[
                ...requiredFieldDefault,
                {
                  validator: matchRegexp,
                  params: "^[0-9]{3,6}$",
                  message: "Die Postleitzahl besteht aus 3-6 Zahlen",
                },
              ]}
              registerValidators={registerValidators}
              error={!!errors["petitioner.residenceAddress.postalCode"]}
              helperText={errors["petitioner.residenceAddress.postalCode"]}
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 6,
              lg: 4,
            }}
          >
            <ValidatorTextField
              name="petitioner.residenceAddress.addressLocality"
              label="Ort"
              value={values["petitioner.residenceAddress.addressLocality"]}
              onChange={handleChange}
              onBlur={handleBlur}
              validators={[...requiredFieldDefault, ...textFieldDefault]}
              registerValidators={registerValidators}
              error={!!errors["petitioner.residenceAddress.addressLocality"]}
              helperText={errors["petitioner.residenceAddress.addressLocality"]}
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 6,
              lg: 4,
            }}
          >
            <ValidatorTextField
              name="telephone"
              label="Ihre Telefonnummer"
              value={values["telephone"]}
              onChange={handleChange}
              onBlur={handleBlur}
              registerValidators={registerValidators}
              validators={[
                ...requiredFieldDefault,
                {
                  validator: matchRegexp,
                  params: "^[0-9 -/+]*$",
                  message: "Das Format der Telefonnummer ist ungültig!",
                },
              ]}
              error={!!errors["telephone"]}
              helperText={errors["telephone"]}
            />
          </Grid>
          <Grid size={12}>
            <SubmitButton
              error={_.keys(errors).length > 0}
              isLoading={isLoading}
              disabled={_.keys(touchedValues).length === 0}
              variant={"contained"}
              type={"submit"}
            >
              Änderungen speichern
            </SubmitButton>
          </Grid>
        </Grid>
      </ValidatorForm>
    </>
  );
};

export default Petitioner;
